/**
 * CRS Reconciliation Wizard — Phase D / LAYER-LIFECYCLE-ALIGNMENT.
 *
 * Three-step modal:
 *   Step 1 — Auto-detect: read-only summary of source/target CRS + directive.
 *   Step 2 — Triage: card grid of 5 reconciliation options.
 *   Step 3 — Confirm: summary of chosen option + confirm/back buttons.
 *
 * Presentation-only: receives `triage: CrsReconciliation` and emits
 * `onDecision(ReconciliationDecision)`. Does NOT call any platform API.
 * The parent wires the real `POST /align/preview` and persists the
 * `alignedTo` edge in the Object Index via the platform SDK.
 *
 * Token-only: every visual constant is sourced from `tokens.*`.
 * No raw hex / px / em values.
 *
 * Accessibility:
 *   - `role="dialog"`, `aria-modal="true"`, `aria-labelledby`, `aria-describedby`.
 *   - Focus trap + Escape to close (delegated to the `<Dialog>` primitive).
 *   - Option cards: Tab to traverse, Enter/Space to select.
 *   - 40px minimum touch target on all interactive elements.
 *   - Non-color cue on every status indicator (icon + color).
 */

import { useId, useState } from 'react';
import { Callout, Dialog, Icon, tokens, type DialogAction } from '@/design';
import type {
  CrsReconciliation,
  ReconciliationDecision,
  ReconciliationOption,
  ReconciliationOptionKey,
} from './wizardTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CrsReconciliationWizardProps {
  open: boolean;
  triage: CrsReconciliation;
  /** Identity of the deciding user — written into ReconciliationDecision. */
  currentUser?: string;
  onClose: () => void;
  onDecision: (decision: ReconciliationDecision) => void;
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Step enum
// ---------------------------------------------------------------------------

type WizardStep = 1 | 2 | 3;

// ---------------------------------------------------------------------------
// Directive chip helper
// ---------------------------------------------------------------------------

const DIRECTIVE_ICON: Record<CrsReconciliation['directive'], Parameters<typeof Icon>[0]['name']> = {
  identity: 'check_circle',
  // compare_arrows is the registered icon for a bidirectional CRS comparison
  reproject: 'compare_arrows',
  reject: 'error',
};

const DIRECTIVE_LABEL: Record<CrsReconciliation['directive'], string> = {
  identity: 'Identity — no reprojection required',
  reproject: 'Reproject — source CRS differs from project frame',
  reject: 'Reject — CRS is missing or cannot be resolved',
};

// ---------------------------------------------------------------------------
// Styles (token-driven inline styles)
// ---------------------------------------------------------------------------

const s = {
  /** Step header chip */
  stepChip: (active: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: tokens.density.touchMin,
    height: tokens.density.touchMin,
    borderRadius: tokens.radius.pill,
    background: active ? tokens.color.accent.primary : tokens.color.surface[3],
    color: active ? tokens.color.accent.on : tokens.color.ink.muted,
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.label.size,
    fontWeight: tokens.font.weight.semibold,
  }),

  stepLabel: (active: boolean) => ({
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.label.size,
    fontWeight: active ? tokens.font.weight.semibold : tokens.font.weight.regular,
    color: active ? tokens.color.ink.primary : tokens.color.ink.muted,
  }),

  /** CRS label row */
  crsRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.space[1],
    padding: tokens.space[3],
    background: tokens.color.surface[2],
    borderRadius: tokens.radius.md,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    fontFamily: tokens.font.family.ui,
  },

  crsLabel: {
    fontSize: tokens.font.role.label.size,
    fontWeight: tokens.font.weight.medium,
    color: tokens.color.ink.secondary,
  },

  crsValue: {
    fontSize: tokens.font.role.engTag.size,
    fontFamily: tokens.font.family.mono,
    fontWeight: tokens.font.weight.semibold,
    color: tokens.color.ink.primary,
  },

  /** Option card */
  optionCard: (selected: boolean, available: boolean) => ({
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: tokens.space[2],
    padding: tokens.space[3],
    background: selected
      ? tokens.color.accent.subtle
      : available
        ? tokens.color.surface[2]
        : tokens.color.surface[1],
    border: selected
      ? `${tokens.border.width.thin + 1}px solid ${tokens.color.accent.primary}`
      : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.lg,
    cursor: available ? 'pointer' : 'default',
    opacity: available ? 1 : 0.48,
    minHeight: tokens.density.touchMin,
    transition: `background ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard},
                 border-color ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}`,
  }),

  optionTitle: {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.body.size,
    fontWeight: tokens.font.weight.semibold,
    color: tokens.color.ink.primary,
    lineHeight: tokens.font.lineHeight.tight,
  },

  optionSubtitle: {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.engRow.size,
    color: tokens.color.ink.secondary,
    lineHeight: tokens.font.lineHeight.normal,
  },

  metaGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${tokens.space[1]}px ${tokens.space[3]}px`,
  },

  metaKey: {
    fontSize: tokens.font.role.engMeta.size,
    color: tokens.color.ink.muted,
    fontFamily: tokens.font.family.ui,
  },

  metaValue: {
    fontSize: tokens.font.role.engMeta.size,
    color: tokens.color.ink.secondary,
    fontFamily: tokens.font.family.ui,
  },

  recommendedBadge: {
    position: 'absolute' as const,
    top: tokens.space[2],
    right: tokens.space[2],
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.space[1],
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    background: tokens.color.accent.primary,
    color: tokens.color.accent.on,
    borderRadius: tokens.radius.pill,
    fontSize: tokens.font.role.engMeta.size,
    fontWeight: tokens.font.weight.semibold,
    fontFamily: tokens.font.family.ui,
  },

  /** Confirm step summary row */
  confirmRow: (label: string) => ({
    label,
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: tokens.space[3],
      fontSize: tokens.font.role.body.size,
      fontFamily: tokens.font.family.ui,
      padding: `${tokens.space[2]}px 0`,
      borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    },
  }),
} as const;

// ---------------------------------------------------------------------------
// Step 1 — Auto-detect
// ---------------------------------------------------------------------------

function Step1Content({
  triage,
  descId,
}: {
  triage: CrsReconciliation;
  descId: string;
}) {
  const dirIcon = DIRECTIVE_ICON[triage.directive];
  const dirLabel = DIRECTIVE_LABEL[triage.directive];
  const isReject = triage.directive === 'reject';

  return (
    <div
      id={descId}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}
    >
      <p
        style={{
          margin: 0,
          fontSize: tokens.font.role.body.size,
          color: tokens.color.ink.secondary,
          fontFamily: tokens.font.family.ui,
        }}
      >
        The platform&apos;s <code style={{ fontFamily: tokens.font.family.mono }}>align_for_pin</code> service
        has analysed the incoming layer against the project anchor. Review the result below before
        choosing an alignment strategy.
      </p>

      {/* Source / Target CRS */}
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.space[3] }}
        aria-label="CRS detection summary"
      >
        <div style={s.crsRow}>
          <span style={s.crsLabel}>Source CRS</span>
          <span
            data-testid="wizard-source-crs"
            style={s.crsValue}
          >
            {triage.source.crs || '— missing —'}
          </span>
          <span style={s.metaValue}>{triage.source.coordinateMode}</span>
        </div>
        <div style={s.crsRow}>
          <span style={s.crsLabel}>Project anchor</span>
          <span
            data-testid="wizard-target-crs"
            style={s.crsValue}
          >
            {triage.target.horizontalCrs || `(${triage.target.kind})`}
          </span>
          <span style={s.metaValue}>kind: {triage.target.kind}</span>
        </div>
      </div>

      {/* Directive chip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[2],
          padding: tokens.space[3],
          background: isReject ? tokens.color.surface[2] : tokens.color.surface[2],
          borderLeft: `${tokens.border.width.thin * 4}px solid ${
            isReject
              ? tokens.color.semantic.danger
              : triage.directive === 'reproject'
                ? tokens.color.semantic.warning
                : tokens.color.semantic.success
          }`,
          borderRadius: tokens.radius.md,
          fontFamily: tokens.font.family.ui,
        }}
        role="note"
        aria-label={`Directive: ${dirLabel}`}
      >
        <Icon
          name={dirIcon}
          size={20}
          aria-label={triage.directive}
        />
        <span
          style={{
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.primary,
          }}
        >
          {dirLabel}
        </span>
        {triage.errorCode ? (
          <code
            style={{
              marginLeft: 'auto',
              fontSize: tokens.font.role.engMeta.size,
              fontFamily: tokens.font.family.mono,
              color: tokens.color.semantic.danger,
            }}
          >
            {triage.errorCode}
          </code>
        ) : null}
      </div>

      {triage.reason ? (
        <Callout severity={isReject ? 'error' : 'info'}>
          {triage.reason}
        </Callout>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Triage (option card grid)
// ---------------------------------------------------------------------------

function OptionCard({
  option,
  selected,
  onSelect,
}: {
  option: ReconciliationOption;
  selected: boolean;
  onSelect: (key: ReconciliationOptionKey) => void;
}) {
  const cardId = useId();

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!option.available) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(option.key);
    }
  }

  return (
    <div
      id={cardId}
      role="radio"
      aria-checked={selected}
      aria-disabled={!option.available}
      title={option.available ? undefined : option.unavailableReason}
      tabIndex={option.available ? 0 : -1}
      data-testid={`crs-option-card-${option.key}`}
      style={s.optionCard(selected, option.available)}
      onClick={() => option.available && onSelect(option.key)}
      onKeyDown={handleKeyDown}
    >
      {option.recommended && option.available ? (
        <span style={s.recommendedBadge} aria-label="Recommended option">
          {/* workspace_premium is the registered registry icon for "best / recommended" */}
          <Icon name="workspace_premium" size={16} decorative />
          Recommended
        </span>
      ) : null}

      <span style={s.optionTitle}>{option.title}</span>
      <span style={s.optionSubtitle}>{option.subtitle}</span>

      <div style={s.metaGrid}>
        <span style={s.metaKey}>What happens</span>
        <span style={s.metaValue}>{option.what}</span>

        <span style={s.metaKey}>Where</span>
        <span style={s.metaValue}>{option.where}</span>

        <span style={s.metaKey}>How long</span>
        <span style={s.metaValue}>{option.howLong}</span>

        <span style={s.metaKey}>Reversible</span>
        <span style={s.metaValue}>{option.reversible}</span>

        <span style={s.metaKey}>Persisted</span>
        <span style={s.metaValue}>{option.persistence}</span>
      </div>
    </div>
  );
}

function Step2Content({
  options,
  selectedKey,
  onSelect,
}: {
  options: readonly ReconciliationOption[];
  selectedKey: ReconciliationOptionKey | null;
  onSelect: (key: ReconciliationOptionKey) => void;
}) {
  // TODO(drift-warning): For `align-viewer`, when triage.source.bbox diagonal
  // exceeds CITY_SCALE_THRESHOLD_KM (TBD by geometry team), render a
  // Callout severity="warning" beneath the align-viewer card explaining the
  // potential for sub-metre drift at city scale. The threshold is not yet
  // defined; add the check here once available (see wizardOptions.ts TODO).
  return (
    <div
      role="radiogroup"
      aria-label="CRS reconciliation options"
      aria-required="true"
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}
    >
      <p
        style={{
          margin: 0,
          fontSize: tokens.font.role.body.size,
          color: tokens.color.ink.secondary,
          fontFamily: tokens.font.family.ui,
        }}
      >
        Choose how this layer&apos;s coordinate system should be aligned to the project frame.
        Unavailable options are shown for context but cannot be selected.
      </p>

      {options.map((option) => (
        <OptionCard
          key={option.key}
          option={option}
          selected={selectedKey === option.key}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Confirm
// ---------------------------------------------------------------------------

function Step3Content({
  triage,
  chosenOption,
}: {
  triage: CrsReconciliation;
  chosenOption: ReconciliationOption;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}>
      <Callout severity="info" title="Review your choice">
        You are about to apply the alignment strategy described below. This action will be recorded
        in the Object Index with your identity and a timestamp.
      </Callout>

      <div
        style={{
          background: tokens.color.surface[2],
          borderRadius: tokens.radius.md,
          padding: tokens.space[3],
          fontFamily: tokens.font.family.ui,
        }}
        aria-label="Decision summary"
      >
        <div style={{ marginBottom: tokens.space[2] }}>
          <span
            style={{
              fontSize: tokens.font.role.label.size,
              fontWeight: tokens.font.weight.semibold,
              color: tokens.color.ink.primary,
            }}
          >
            {chosenOption.title}
          </span>
        </div>

        {(
          [
            ['Source CRS', triage.source.crs || '— missing —'],
            ['Target CRS', triage.target.horizontalCrs || `(${triage.target.kind})`],
            ['What happens', chosenOption.what],
            ['Where', chosenOption.where],
            ['How long', chosenOption.howLong],
            ['Reversible', chosenOption.reversible],
            ['Persisted', chosenOption.persistence],
          ] as const
        ).map(([label, value]) => (
          <div
            key={label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: tokens.space[3],
              fontSize: tokens.font.role.engRow.size,
              fontFamily: tokens.font.family.ui,
              padding: `${tokens.space[2]}px 0`,
              borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            }}
          >
            <span style={{ color: tokens.color.ink.muted }}>{label}</span>
            <span
              style={{
                color: tokens.color.ink.secondary,
                textAlign: 'right',
                maxWidth: '60%',
              }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stepper header
// ---------------------------------------------------------------------------

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Auto-detect',
  2: 'Triage',
  3: 'Confirm',
};

function StepperHeader({ step }: { step: WizardStep }) {
  const steps: WizardStep[] = [1, 2, 3];
  return (
    <div
      role="group"
      aria-label="Wizard steps"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        paddingBottom: tokens.space[3],
        borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
      }}
    >
      {steps.map((s, idx) => (
        <div
          key={s}
          style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}
          aria-current={step === s ? 'step' : undefined}
        >
          <span style={s === step ? { ...STEP_CHIP_ACTIVE } : { ...STEP_CHIP_INACTIVE }}>
            {s}
          </span>
          <span
            style={{
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.role.label.size,
              fontWeight: step === s ? tokens.font.weight.semibold : tokens.font.weight.regular,
              color: step === s ? tokens.color.ink.primary : tokens.color.ink.muted,
            }}
          >
            {STEP_LABELS[s]}
          </span>
          {idx < steps.length - 1 ? (
            <Icon
              name="chevron_right"
              size={16}
              decorative
              aria-hidden="true"
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

const STEP_CHIP_ACTIVE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: tokens.density.touchMin,
  height: tokens.density.touchMin,
  borderRadius: tokens.radius.pill,
  background: tokens.color.accent.primary,
  color: tokens.color.accent.on,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.role.label.size,
  fontWeight: tokens.font.weight.semibold,
};

const STEP_CHIP_INACTIVE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: tokens.density.touchMin,
  height: tokens.density.touchMin,
  borderRadius: tokens.radius.pill,
  background: tokens.color.surface[3],
  color: tokens.color.ink.muted,
  fontFamily: tokens.font.family.ui,
  fontSize: tokens.font.role.label.size,
  fontWeight: tokens.font.weight.regular,
};

// ---------------------------------------------------------------------------
// Main wizard component
// ---------------------------------------------------------------------------

export function CrsReconciliationWizard({
  open,
  triage,
  currentUser = 'unknown',
  onClose,
  onDecision,
  'data-testid': testId,
}: CrsReconciliationWizardProps) {
  const [step, setStep] = useState<WizardStep>(1);
  const [selectedKey, setSelectedKey] = useState<ReconciliationOptionKey | null>(
    // Pre-select the recommended option.
    triage.recommendation ?? null,
  );

  const descId = useId();
  void useId();

  if (!open) return null;

  const chosenOption = triage.options.find((o) => o.key === selectedKey) ?? null;

  // ---- Dialog title per step ----
  const STEP_TITLE: Record<WizardStep, string> = {
    1: 'CRS Reconciliation — Auto-detect',
    2: 'CRS Reconciliation — Choose alignment',
    3: 'CRS Reconciliation — Confirm',
  };

  // ---- Actions per step ----
  const actions: DialogAction[] = (() => {
    if (step === 1) {
      return [
        { label: 'Cancel', variant: 'ghost', onClick: onClose },
        {
          label: 'Next: Choose alignment',
          variant: 'primary',
          onClick: () => setStep(2),
        },
      ];
    }
    if (step === 2) {
      return [
        { label: 'Back', variant: 'ghost', onClick: () => setStep(1) },
        { label: 'Cancel', variant: 'ghost', onClick: onClose },
        {
          label: 'Next: Confirm',
          variant: 'primary',
          onClick: () => {
            if (chosenOption) setStep(3);
          },
          defaultFocus: false,
        },
      ];
    }
    // step === 3
    return [
      { label: 'Back', variant: 'ghost', onClick: () => setStep(2) },
      { label: 'Cancel', variant: 'ghost', onClick: onClose },
      {
        label: 'Apply alignment',
        variant: 'primary',
        onClick: () => {
          if (!chosenOption) return;
          const decision: ReconciliationDecision = {
            optionKey: chosenOption.key,
            viewerMatrix: chosenOption.key === 'align-viewer' ? null : undefined,
            scaleMode: chosenOption.key === 'align-viewer' ? 'visual' : undefined,
            decisionAt: new Date().toISOString(),
            decisionBy: currentUser,
          };
          onDecision(decision);
        },
      },
    ];
  })();

  return (
    <Dialog
      title={STEP_TITLE[step]}
      size="lg"
      onClose={onClose}
      actions={actions}
      data-testid={testId ?? 'crs-reconciliation-wizard'}
    >
      <div
        id={descId}
        style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[4] }}
      >
        <StepperHeader step={step} />

        {step === 1 ? (
          <Step1Content triage={triage} descId={`${descId}-step1`} />
        ) : null}

        {step === 2 ? (
          <Step2Content
            options={triage.options}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
          />
        ) : null}

        {step === 3 && chosenOption ? (
          <Step3Content triage={triage} chosenOption={chosenOption} />
        ) : null}

        {step === 2 && !chosenOption ? (
          <Callout severity="info">
            Select one of the options above to continue.
          </Callout>
        ) : null}
      </div>
    </Dialog>
  );
}
